import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import {
  DEPOSIT_METHODS,
  WITHDRAWAL_METHODS,
  formatMoney,
  formatDateTime,
  transactionLabel,
  transactionIcon,
} from '@/lib/wallet';
import type { Wallet, WalletTransaction, TransactionType } from '@/lib/wallet';

type ActionTab = 'deposit' | 'withdrawal' | 'transfer';

interface Recipient {
  id: string;
  full_name: string | null;
  email: string | null;
}

function WalletPanel() {
  const { user } = useAuth();

  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [tab, setTab] = useState<ActionTab>('deposit');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(DEPOSIT_METHODS[0]);
  const [submitting, setSubmitting] = useState(false);

  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [searching, setSearching] = useState(false);
  const [transferNote, setTransferNote] = useState('');

  const loadWallet = useCallback(async () => {
    if (!user) return;
    setError(null);
    try {
      const { data, error: walletError } = await supabase.rpc('ensure_wallet');
      if (walletError) throw walletError;
      setWallet(data as Wallet | null);

      const { data: txData, error: txError } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (txError) throw txError;
      setTransactions((txData as WalletTransaction[]) || []);
    } catch (err) {
      setError('Impossible de charger votre portefeuille.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadWallet();
    } else {
      setLoading(false);
    }
  }, [user, loadWallet]);

  const handleTabChange = (next: ActionTab) => {
    setTab(next);
    setMethod(next === 'deposit' ? DEPOSIT_METHODS[0] : WITHDRAWAL_METHODS[0]);
    setRecipient(null);
    setRecipientEmail('');
    setTransferNote('');
    setError(null);
    setSuccess(null);
  };

  const handleSearchRecipient = async () => {
    if (!recipientEmail.trim()) {
      setError('Veuillez saisir l\'adresse email du destinataire.');
      return;
    }
    setError(null);
    setSuccess(null);
    setRecipient(null);
    setSearching(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('find_recipient_by_email', {
        p_email: recipientEmail.trim(),
      });
      if (rpcError) {
        const msg = rpcError.message || '';
        if (msg.toLowerCase().includes('not found')) {
          setError('Aucun utilisateur WorldApeiron trouvé avec cette adresse email.');
        } else {
          setError('Impossible de rechercher cet utilisateur. Veuillez réessayer.');
        }
        return;
      }
      setRecipient(data as Recipient);
    } catch {
      setError('Une erreur est survenue lors de la recherche.');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const value = Number(amount);
    if (!amount || Number.isNaN(value) || value <= 0) {
      setError('Veuillez saisir un montant valide.');
      return;
    }

    setError(null);
    setSuccess(null);

    if (tab === 'transfer') {
      if (!recipient) {
        setError('Recherchez d\'abord un destinataire valide.');
        return;
      }
      setSubmitting(true);
      try {
        const { error: rpcError } = await supabase.rpc('transfer_to_user', {
          p_recipient_id: recipient.id,
          p_amount: value,
          p_description: transferNote.trim() ? transferNote.trim() : null,
        });

        if (rpcError) {
          const msg = rpcError.message || '';
          if (msg.toLowerCase().includes('insufficient')) {
            setError('Solde insuffisant pour effectuer ce transfert.');
          } else if (msg.toLowerCase().includes('yourself')) {
            setError('Vous ne pouvez pas transférer de l\'argent à vous-même.');
          } else if (msg.toLowerCase().includes('not found')) {
            setError('Destinataire introuvable.');
          } else if (msg.toLowerCase().includes('positive')) {
            setError('Le montant doit être supérieur à zéro.');
          } else {
            setError('Une erreur est survenue. Veuillez réessayer.');
          }
          return;
        }

        setSuccess(
          `Transfert de ${formatMoney(value)} envoyé à ${recipient.full_name || recipient.email} !`
        );
        setAmount('');
        setRecipient(null);
        setRecipientEmail('');
        setTransferNote('');
        await loadWallet();
      } catch {
        setError('Une erreur est survenue. Veuillez réessayer.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);

    try {
      const type: TransactionType = tab;
      const { error: rpcError } = await supabase.rpc('create_wallet_transaction', {
        p_type: type,
        p_amount: value,
        p_method: method,
        p_description: tab === 'deposit' ? 'Dépôt portefeuille' : 'Retrait portefeuille',
      });

      if (rpcError) {
        const msg = rpcError.message || '';
        if (msg.toLowerCase().includes('insufficient')) {
          setError('Solde insuffisant pour effectuer ce retrait.');
        } else if (msg.toLowerCase().includes('positive')) {
          setError('Le montant doit être supérieur à zéro.');
        } else {
          setError('Une erreur est survenue. Veuillez réessayer.');
        }
        return;
      }

      setSuccess(
        tab === 'deposit'
          ? `Dépôt de ${formatMoney(value)} effectué avec succès !`
          : `Retrait de ${formatMoney(value)} effectué avec succès !`
      );
      setAmount('');
      await loadWallet();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const methodOptions = tab === 'deposit' ? DEPOSIT_METHODS : WITHDRAWAL_METHODS;

  return (
    <div>
      {/* Balance card */}
      <div className="rounded-xl bg-gradient-to-br from-primary-500/15 to-accent-500/10 border border-background-200/70 p-6 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-foreground-400 mb-1">Solde disponible</p>
            <p className="font-heading font-bold text-3xl md:text-4xl text-foreground-50">
              {loading ? '…' : formatMoney(wallet?.balance ?? 0)}
            </p>
            <p className="text-xs text-foreground-400 mt-2 flex items-center gap-1.5">
              <i className="ri-shield-check-line text-primary-400" />
              Portefeuille sécurisé · {transactions.length} transaction{transactions.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold">
              {wallet?.currency || 'XOF'}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-accent-100/70 text-accent-900 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-900 text-sm">{success}</div>
      )}

      {/* Action card */}
      <div className="rounded-xl bg-background-50 border border-background-200/70 p-5 md:p-6 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <h3 className="font-heading font-semibold text-lg text-foreground-50">Opération</h3>
          <div className="inline-flex items-center bg-background-100 border border-background-200/70 rounded-full p-1">
            <button
              type="button"
              onClick={() => handleTabChange('deposit')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                tab === 'deposit'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-400 hover:text-foreground-50'
              }`}
            >
              Dépôt
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('withdrawal')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                tab === 'withdrawal'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-400 hover:text-foreground-50'
              }`}
            >
              Retrait
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('transfer')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                tab === 'transfer'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-400 hover:text-foreground-50'
              }`}
            >
              Transfert
            </button>
          </div>
        </div>

        {tab === 'transfer' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Recipient search */}
            <div>
              <label htmlFor="recipient" className="block text-sm text-foreground-400 mb-2">
                Destinataire (email WorldApeiron)
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="recipient"
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => {
                    setRecipientEmail(e.target.value);
                    setRecipient(null);
                  }}
                  placeholder="ex : nom@email.com"
                  className="flex-1 px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
                />
                <button
                  type="button"
                  onClick={handleSearchRecipient}
                  disabled={searching}
                  className="px-5 py-3 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors disabled:opacity-60 whitespace-nowrap text-sm"
                >
                  {searching ? 'Recherche…' : 'Vérifier'}
                </button>
              </div>

              {recipient && (
                <div className="mt-3 p-3 rounded-lg bg-primary-500/10 border border-primary-300/40 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 font-heading font-bold">
                    {(recipient.full_name || recipient.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground-50">
                      {recipient.full_name || 'Utilisateur'}
                    </p>
                    <p className="text-xs text-foreground-400">{recipient.email}</p>
                  </div>
                  <i className="ri-checkbox-circle-fill text-xl text-primary-400" />
                </div>
              )}
            </div>

            {/* Amount */}
            <div>
              <label htmlFor="transfer-amount" className="block text-sm text-foreground-400 mb-2">
                Montant (FCFA)
              </label>
              <input
                id="transfer-amount"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ex : 5000"
                className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
              />
            </div>

            {/* Note */}
            <div>
              <label htmlFor="transfer-note" className="block text-sm text-foreground-400 mb-2">
                Message (optionnel)
              </label>
              <input
                id="transfer-note"
                type="text"
                maxLength={120}
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="Ex : Merci pour le café !"
                className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-5 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap text-sm"
            >
              {submitting ? 'Envoi…' : 'Envoyer l\'argent'}
            </button>

            <p className="text-xs text-foreground-400 flex items-center gap-1.5">
              <i className="ri-information-line" />
              Le transfert est instantané et irréversible. Vérifiez bien l'adresse du destinataire avant d'envoyer.
            </p>
          </form>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-1">
                <label htmlFor="amount" className="block text-sm text-foreground-400 mb-2">
                  Montant (FCFA)
                </label>
                <input
                  id="amount"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Ex : 5000"
                  className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400"
                />
              </div>

              <div className="md:col-span-1">
                <label htmlFor="method" className="block text-sm text-foreground-400 mb-2">
                  Méthode
                </label>
                <select
                  id="method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
                >
                  {methodOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-5 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap text-sm"
                >
                  {submitting ? 'Traitement…' : tab === 'deposit' ? 'Déposer' : 'Retirer'}
                </button>
              </div>
            </form>

            <p className="text-xs text-foreground-400 mt-4 flex items-center gap-1.5">
              <i className="ri-information-line" />
              {tab === 'deposit'
                ? 'Les dépôts sont simulés pour le moment. Une passerelle de paiement réelle (Mobile Money, carte) pourra être branchée.'
                : 'Le retrait est déduit immédiatement de votre solde disponible.'}
            </p>
          </>
        )}
      </div>

      {/* Transactions history */}
      <div className="rounded-xl bg-background-50 border border-background-200/70 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-history-line text-xl text-primary-400" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground-50">Historique</h3>
            <p className="text-xs text-foreground-400">Vos dernières transactions</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[160px]">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center rounded-xl border border-dashed border-background-300/60">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
              <i className="ri-wallet-3-line text-2xl text-foreground-400" />
            </div>
            <p className="font-heading font-semibold text-foreground-50 mb-1">
              Aucune transaction pour le moment
            </p>
            <p className="text-sm text-foreground-400">
              Effectuez un dépôt pour commencer à utiliser votre portefeuille.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((t) => {
              const isCredit = t.direction ? t.direction === 'credit' : t.type === 'deposit';
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div
                    className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-lg ${
                      isCredit ? 'bg-primary-500/10' : 'bg-accent-100/70'
                    }`}
                  >
                    <i
                      className={`${transactionIcon(t.type)} text-lg ${
                        isCredit ? 'text-primary-400' : 'text-accent-500'
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-50">
                      {transactionLabel(t.type)}
                      {t.counterparty_name ? ` · ${t.counterparty_name}` : t.method ? ` · ${t.method}` : ''}
                    </p>
                    {t.description && (
                      <p className="text-xs text-foreground-400 truncate">{t.description}</p>
                    )}
                    <p className="text-xs text-foreground-400">{formatDateTime(t.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p
                      className={`text-sm font-semibold ${
                        isCredit ? 'text-primary-400' : 'text-foreground-50'
                      }`}
                    >
                      {isCredit ? '+' : '−'}
                      {formatMoney(t.amount)}
                    </p>
                    {t.balance_after !== null && (
                      <p className="text-xs text-foreground-400">
                        Solde : {formatMoney(t.balance_after)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default WalletPanel;